import { useEffect, useMemo } from 'react';
import { Modal, Form, Select, Checkbox, Alert, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';

interface Props { open: boolean; onClose: () => void; }

/**
 * Transfer a Sales Head's oversight to a successor: their territory bindings move across and
 * (optionally) their direct-report Sales Persons re-point to the successor. SPs keep their
 * own beats — only who they report to changes.
 */
export function SHTransferModal({ open, onClose }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const fromShId = Form.useWatch('fromShId', form);

  const { data: heads } = useQuery({
    queryKey: ['sh-list-all'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_HEAD', pageSize: 200 } }),
    enabled: open,
  });
  const options = useMemo(() => (heads?.data ?? []).filter((u: any) => u.status === 'active').map((u: any) => ({ value: String(u.id), label: u.fullName })), [heads]);

  useEffect(() => { if (open) form.setFieldsValue({ fromShId: undefined, toShId: undefined, reassignReports: true }); }, [open, form]);

  const transfer = useMutation({
    mutationFn: (v: any) => request({ url: '/territory-assignments/sh-transfer', method: 'POST', data: { fromShId: v.fromShId, toShId: v.toShId, reassignReports: v.reassignReports } }),
    onSuccess: (res: any) => {
      message.success(`Transferred ${res?.territories ?? 0} territory binding(s)${res?.reports ? ` + ${res.reports} report(s)` : ''}`);
      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to transfer'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose() }} title="Transfer Sales Head"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={transfer.isPending} onClick={() => form.submit()}>Transfer</Button></>}>
      <Form form={form} layout="vertical" onFinish={(v) => transfer.mutate(v)}>
        <Form.Item name="fromShId" label="From (outgoing Sales Head)" rules={[{ required: true, message: 'Pick the outgoing Sales Head' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Outgoing sales head"
            onChange={() => form.setFieldsValue({ toShId: undefined })} options={options} />
        </Form.Item>
        <Form.Item name="toShId" label="To (successor)" rules={[{ required: true, message: 'Pick the successor Sales Head' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Successor sales head" disabled={!fromShId}
            options={options.filter((o: any) => o.value !== String(fromShId))} />
        </Form.Item>
        <Form.Item name="reassignReports" valuePropName="checked">
          <Checkbox label="Also re-point the outgoing head's Sales Persons to the successor" />
        </Form.Item>
        <Alert type="info" title="Sales Persons keep their own beats and customers — only their territory oversight and reporting line move." />
      </Form>
    </Modal>
  );
}
